import { COMMA, ENTER, SPACE } from "@angular/cdk/keycodes";
import { ChangeDetectionStrategy, Component, computed, inject, model, OnInit, signal, ViewEncapsulation } from "@angular/core";
import { toObservable, toSignal } from "@angular/core/rxjs-interop";
import { FormControl, FormsModule, ReactiveFormsModule } from "@angular/forms";
import { MatAutocompleteModule } from "@angular/material/autocomplete";
import { MatButtonModule } from "@angular/material/button";
import { MatChipInputEvent, MatChipsModule } from "@angular/material/chips";
import { MatDatepicker, MatDatepickerModule } from "@angular/material/datepicker";
import { MatDialogRef, MAT_DIALOG_DATA } from "@angular/material/dialog";
import { MatDividerModule } from "@angular/material/divider";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatIconModule } from "@angular/material/icon";
import { MatInputModule } from "@angular/material/input";
import { MatRadioModule } from "@angular/material/radio";
import { MatSelectModule } from "@angular/material/select";
import { MatSlideToggleModule } from "@angular/material/slide-toggle";
import { SelectOption } from "@grupo-constant/next-dtype/types";
import { TranslocoModule, TranslocoService } from "@ngneat/transloco";
import { format, parse, startOfToday } from "date-fns";
import { derivedAsync } from "ngxtension/derived-async";
import { debounceTime, firstValueFrom, map, Observable, of, Subject, switchMap, takeUntil } from "rxjs";
import { DialogsService } from "../dialogs.service";
import { LocalizePipe } from "../../core/pipes/standalone/localize.pipe";
import { WorkerChiplistAcComponent } from "../../core/components/worker-chiplist-ac/worker-chiplist-ac.component";
import { DispatchModeEnum, Net4AbsenceConcept, Net4ConceptMapping, Net4FormatsEnum, Net4Worker } from "../../modules/admin/endpoints/endpoints.types";
import { Net2BeepleLocator } from "../../core/dtype/dtype.types";
import { EndpointsService } from "../../modules/admin/endpoints/endpoints.service";
import { FuseConfirmationService } from "../../../@fuse/services/confirmation/confirmation.service";
import { HttpService } from "../../core/http/http.service";
import { LocalizeService } from "../../core/pipes/localize.service";
import { MasterService } from "../../core/master/master.service";
import { Utils } from "../../core/utils/utils";
import { Net2BeepleMethodsEnum } from "../../core/enums/net2beeple-methods.enum";

@Component({
    selector: 'app-edit-concept-dialog',
    templateUrl: './edit-concept-dialog.component.html',
    styleUrls: ['./edit-concept-dialog.component.scss'],
    imports: [FormsModule, LocalizePipe, MatAutocompleteModule, MatButtonModule, MatChipsModule, MatDatepickerModule, MatDividerModule, MatFormFieldModule, MatIconModule, MatInputModule, MatRadioModule, MatSelectModule, MatSlideToggleModule, ReactiveFormsModule, TranslocoModule,  WorkerChiplistAcComponent],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None
})
export class EditConceptDialogComponent implements OnInit {
    readonly separatorKeysCodes = [ENTER, COMMA, SPACE] as const;
    private readonly formulaEnumClass = 'com.cloudcodice.thirdparty.grupoconstant.net4.conceptmapping.Net4ConceptFormula';
    private readonly net4FormatEnumClass = 'com.cloudcodice.thirdparty.grupoconstant.net4.Net4InputFormats';

    public data: { concept: Net4ConceptMapping, locator: Net2BeepleLocator, workers: Map<number, string>} = inject(MAT_DIALOG_DATA);
    public dialogRef = inject(MatDialogRef<EditConceptDialogComponent>);
    private dialogsService = inject(DialogsService);
    private endpointsService = inject(EndpointsService);
    private _fuseConfirmationService = inject(FuseConfirmationService);
    private httpService = inject(HttpService);
    private localize = inject(LocalizeService);
    private masterService = inject(MasterService);
    private transloco = inject(TranslocoService);

    customizing = signal(false);
    newConcept = signal(false);
    locator = signal(this.data.locator);
    jobMasterUri = computed(() => {
        const locator = this.locator();
        return `master://HS=${locator.HS}/TS=${locator.TS}/OU=01/MasterJobMapping`;
    });

    logicalConcept = model<string>();
    type = model<string>();
    fromDate = model<Date>();
    toDate = model<Date>();
    workplaceCode = model<string>();
    excludeNet4ConceptId = model<string[]>();
    mustBeInBillingLine = model<boolean>();
    score = model<number>();
    dispatchMode = model<DispatchModeEnum>();
    net4Format = model<Net4FormatsEnum>();
    concept = model<string | Net4AbsenceConcept>();
    empresa = model<string | SelectOption>();
    contractType = model<string>('');
    jobContractCode = model<number>();
    jobSupervisor = model<string>('');
    formula = model<string>('');
    roundInterval = model<number | undefined>();
    roundPoint = model<number | undefined>();

    conceptChanged = toSignal(toObservable(this.concept).pipe(debounceTime(500)));
    conceptOptions = derivedAsync<Net4AbsenceConcept[]>(() => {
        const concept = this.conceptChanged();
        if (typeof concept === 'string') {
            return this.endpointsService.searchNet4PresenceConcept({ expr: concept, locator: this.data.locator });
        } else {
            return [];
        }
    });
    empresaOptions = toSignal(this.endpointsService.searchEmpresaBusPartaker().pipe(map(reply => reply.option ?? [])));
    empresaChanged = toSignal(toObservable(this.empresa).pipe(debounceTime(500)));
    filteredEmpresaOptions = computed(() => {
        const empresa = this.empresaChanged();
        const options = this.empresaOptions();
        if (typeof empresa === 'string') {
            if (empresa.length > 0) {
                return options.filter(o => Utils.removeAccents(this.localize.transform(o.value)).toLowerCase().includes(Utils.removeAccents(empresa).toLowerCase()));
            } else {
                return options;
            }
        } else {
            return [];
        }
    });
    jobOption = derivedAsync<SelectOption[]>(() => {
        const uri = this.jobMasterUri();
        return this.masterService.retrieveMasterDataAsSelectOptions(uri);
    });

    formulaOptions = toSignal(this.httpService.post<{ option: SelectOption[] }>(Net2BeepleMethodsEnum.retrieveEnumerationType, { enumerationClass: this.formulaEnumClass })
        .pipe(map(reply => reply.option ?? [])));

    net4FormatOptions = toSignal(this.httpService.post<{ option: SelectOption[] }>(Net2BeepleMethodsEnum.retrieveEnumerationType, { enumerationClass: this.net4FormatEnumClass })
        .pipe(map(reply => reply.option ?? [])));

    searchCtrl: FormControl = new FormControl();
    options$: Observable<any[]>;
    control: FormControl<Net4Worker[] | string> = new FormControl();
    private _unsubscribeAll = new Subject<void>();

    async ngOnInit() {
        this.logicalConcept.set(this.data.concept.logicalConcept);
        this.type.set(this.data.concept.conceptType);
        if (this.data.concept.conceptType === "TIME") {
            this.net4Format.set(this.data.concept.net4Format ?? Net4FormatsEnum.HOURS)
        }
        this.fromDate.set(this.data.concept.from ? parse(this.data.concept.from, 'yyyy-MM-dd', startOfToday()) : undefined);
        this.toDate.set(this.data.concept.to ? parse(this.data.concept.to, 'yyyy-MM-dd', startOfToday()) : undefined);
        this.workplaceCode.set(this.data.concept.workplaceCode);
        this.excludeNet4ConceptId.set(this.data.concept.excludeNet4ConceptId ? this.data.concept.excludeNet4ConceptId : []);
        this.mustBeInBillingLine.set(this.data.concept.mustBeInBillingLine);
        this.score.set(this.data.concept.score);
        this.dispatchMode.set(this.data.concept.dispatchMode);
        this.formula.set(this.data.concept.net4ConceptFormula ?? '');
        this.roundInterval.set(this.data.concept.roundInterval);
        this.roundPoint.set(this.data.concept.roundPoint);
        this.concept.set(this.data.concept.net4ConceptId ? { Codigo: String(this.data.concept.net4ConceptId), Nombre: this.data.concept.net4ConceptName, Prefix: '' } : '');
        this.empresa.set(this.data.concept.empresaId ? { code: String(this.data.concept.empresaId), value: { 'es-ES': this.data.concept.empresaName }, _deprecated: false, export: {} } : '');
        this.control.setValue(this.data.concept.trabajadorNet4Ids
            .map(trabajadorNet4Id => ({ trabajadorNet4Id, displayName: this.getWorkerDisplayName(trabajadorNet4Id)})));
        if (this.data.concept.clienteTipoAsignacion) {
            this.contractType.set(this.data.concept.clienteTipoAsignacion);
            this.customizing.set(true);
        }
        if (this.data.concept.codigoPuestoContrato) {
            this.jobContractCode.set(this.data.concept.codigoPuestoContrato);
            this.customizing.set(true);
        }
        if (this.data.concept.jobSupervisor) {
            this.jobSupervisor.set(this.data.concept.jobSupervisor);
            this.customizing.set(true);
        }
        if (this.data.concept.trabajadorNet4Ids.length > 0) {
            this.customizing.set(true);
        }
        if (this.empresa()) {
            this.customizing.set(true);
        }
        
        this.options$ = this.searchCtrl.valueChanges.pipe(
            takeUntil(this._unsubscribeAll),
            debounceTime(1000),
            switchMap((value) => {
                if (typeof value === 'string' && value.length >= 3) {
                    return this.endpointsService.searchDBNet4Worker({ expr: value, locator: this.data.locator }).pipe(
                        map(workers => workers.map((worker) => {
                            this.data.workers.set(worker.trabajadorNet4Id, worker.displayName);

                            const option: any = worker;
                            option.displayName = this.workerNameAndIdDisplay(worker);
                            option.caption = option.displayName;

                            return option;
                        }))
                    );
                }
                return of([]);
            })
        );
    }

    closeDialog() {
        this.dialogRef.close();
    }

    async accept() {
        const confirm = await this.confirm();
        if (confirm) {
            const concept: Net4ConceptMapping = {
                ...this.data.concept,
                from: this.fromDate() ? format(this.fromDate(), 'yyyy-MM-dd') : undefined,
                to: this.toDate() ? format(this.toDate(), 'yyyy-MM-dd') : undefined,
                net4Format: this.data.concept.conceptType === 'TIME' ? this.net4Format() : null,
                excludeNet4ConceptId: this.excludeNet4ConceptId(),
                dispatchMode: this.dispatchMode(),
                net4ConceptId: typeof this.concept() === 'string' ? undefined : Number((this.concept() as Net4AbsenceConcept).Codigo),
                net4ConceptName: typeof this.concept() === 'string' ? undefined : (this.concept() as Net4AbsenceConcept).Nombre,
                empresaId: typeof this.empresa() === 'string' ? undefined : Number((this.empresa() as SelectOption).code),
                empresaName: typeof this.empresa() === 'string' ? undefined : this.localize.transform((this.empresa() as SelectOption).value),
                clienteTipoAsignacion: this.contractType(),
                codigoPuestoContrato: this.jobContractCode(),
                jobSupervisor: this.jobSupervisor(),
                trabajadorNet4Ids: typeof this.control.value === 'string' ? undefined : this.control.value.map((w: Net4Worker) => w.trabajadorNet4Id),
                net4ConceptFormula: this.formula(),
                roundInterval: this.roundInterval(),
                roundPoint: this.roundPoint()
            };
            if (this.newConcept()) {
                delete concept.oid;
            }
            try {
                const reply = await firstValueFrom(this.endpointsService.flushNet4ConceptMapping({ locator: this.data.locator, conceptMapping: concept }));
                this.dialogRef.close(reply.conceptMapping);
            } catch (error) {
                console.error(error);
                this.dialogsService.openErrorDialog({
                    message: error?.error?.echo && Object.keys(error?.error?.echo).length > 0 ? this.localize.transform(error?.error?.echo) : undefined,
                });
            }
        }
    }

    conceptACDisplay(concept: string | Net4AbsenceConcept) {
        return concept ? typeof concept === 'string' ? concept : concept.Nombre : '';
    }

    companyACDisplay = (empresa: string | SelectOption) => {
        return empresa ? typeof empresa === 'string' ? empresa : this.localize.transform(empresa.value) : '';
    }

    workerACDisplay(worker: string | Net4Worker) {
        return worker ? typeof worker === 'string' ? worker : worker.displayName : '';
    }

    workerNameAndIdDisplay(worker: Net4Worker): string {
        return worker ? worker.displayName ? worker.displayName + " (" + worker.trabajadorNet4Id + ")" : worker.trabajadorNet4Id.toString() : '';
    }

    cancelCustomizing() {
        this.customizing.set(false);
        this.contractType.set('');
        this.jobContractCode.set(undefined);
        this.control.setValue('');
    }

    openDatepickerOnClick(datepicker: MatDatepicker<Date>) {
        if (!datepicker.opened) {
            datepicker.open();
        }
    }

    addExclusion(event: MatChipInputEvent) {
        if (event.value) {
            this.excludeNet4ConceptId.update(excludedConcepts => {
                if (!excludedConcepts.includes(event.value)) {
                    excludedConcepts.push(event.value);
                }
                return [...excludedConcepts];
            });
        }
        event.chipInput.clear();
    }

    removeExclusion(index: number) {
        if (index > -1) {
            this.excludeNet4ConceptId.update(excludedConcepts => {
                excludedConcepts.splice(index, 1);
                return [...excludedConcepts];
            });
        }
    }

    getWorkerDisplayName(id: number): string {
        const name = this.data.workers.get(id);
        if (name)
            return name + " (" + id + ")";
        else
            return id.toString();
    }

    private confirm(confirm?: string, cancel?: string) {
        const dialogRef = this._fuseConfirmationService.open({
            title: this.transloco.translate('warning'),
            message: this.transloco.translate('presenceConcepts.confirmSaveConcept'),
            actions: {
                confirm: {
                    label: confirm ? confirm : this.transloco.translate('accept')
                },
                cancel: {
                    label: cancel ? cancel : this.transloco.translate('cancel')
                }
            }
        });

        return new Promise<boolean>((resolve) => {
            dialogRef.afterClosed().subscribe((result) => {
                if (result === 'confirmed') {
                    resolve(true);
                } else {
                    resolve(false);
                }
            });
        });
    }

    ngOnDestroy() {
        this._unsubscribeAll.next();
        this._unsubscribeAll.complete();
    }
}